import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
  Activity, BellRing, ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, Code2, Copy, FileCode2, FileText, Fingerprint, Folder, FolderOpen, Globe,
  History, KeyRound, LayoutGrid, MessageSquare, Pencil, Play, Plus, RefreshCw, Save,
  Search, Send, Server, Settings2, TerminalSquare, Trash2, Wifi,
} from 'lucide-react';
import { CodeEditor } from './CodeEditor';
import { api, ApiClientError, jsonBody } from './api';
import { Modal } from './Modal';
import { Select } from './Select';
import { ConfirmDialog, PromptDialog } from './Dialogs';
import { LogStream } from './LogStream';
import { IdentifyLogs } from './IdentifyLogs';
import { DeviceConsole } from './DeviceConsole';

type Device = {
  id: string;
  owner_id: string;
  name: string;
  fqdn: string | null;
  identify: string | null;
  network_mode: 'ddns' | 'ip_only';
  status: string;
  group_id: string | null;
  notes: string | null;
  last_ip: string | null;
  last_seen_at: string | null;
  ip_reported_at: string | null;
  created_at: string;
  updated_at: string;
};

type Group = { id: string; name: string; parent_id: string | null; device_count: number };
type Template = { id: string; name: string; description: string; current_version: number };

type DeviceKey = {
  id: string;
  name: string;
  key_id: string;
  key_prefix: string;
  provisioning_mode: string;
  status: string;
  permissions: string[];
  activation_expires_at: string | null;
  created_at: string;
};

type Credential = {
  mode: string;
  token?: string;
  token_prefix?: string;
  key?: string;
  key_id?: string;
  key_prefix?: string;
  activation_expires_at?: string;
  expires_at?: string;
};

type InstallCommand = {
  mode: string;
  command: string;
  fetch?: 'curl' | 'wget';
  commands?: Partial<Record<'curl' | 'wget', string>>;
  expires_at?: string;
};

type FileEntry = {
  id: string;
  name: string;
  kind: 'file' | 'folder';
  size_bytes: number;
  content_type: string | null;
  updated_at: string;
};

type DeviceMessage = {
  id: string;
  title: string;
  content_type: string;
  content: string;
  status: string;
  next_available_at: string;
  created_at: string;
};

type DeviceScript = {
  id: string;
  name: string;
  description: string;
  language: string;
  active_version: number | null;
  url?: string;
  created_at: string;
  updated_at: string;
};

type ScriptRun = {
  id: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  version: number;
  exit_code: number | null;
  output: string;
  error_code?: string;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
};

type ScriptVersion = {
  id: string;
  script_id: string;
  version: number;
  sha256: string;
  created_at: string;
  content?: string;
};

type InstallVersion = { version: string; status: string; sha256: string; created_at: string };
type InstallAsset = { version: string; script: string; sha256: string; updated_at: string };
type BootVersion = { version: string; type: string; mode: string; status: string; sha256: string; created_at: string };
type BootAsset = { version: string; type: string; mode: string; script: string; sha256: string; settings: Record<string, unknown>; updated_at: string };
type BootRun = { id: number; boot_version: string; status: string; exit_code: number | null; duration_ms: number; stdout: string; stderr: string; error_code?: string; created_at: string };

type IPInfo = {
  ip: string;
  type: string;
  source: string;
  reported_at: string;
};

type IPHistoryItem = {
  id: number;
  ip: string;
  type: string;
  source: string;
  reported_at: string;
  changed: boolean;
};

type ForwardingRule = {
  id: string;
  name: string;
  enabled: boolean;
  source: string;
  channel: string;
  filter: { event_type?: string; levels?: string[]; message_regex?: string };
  rate_limit_per_min: number;
  cooldown_seconds: number;
};

type CopyStats = {
  scripts: number;
  install_versions: number;
  boot_versions: number;
  forward_rules: number;
};

type Notify = (type: 'success' | 'error', text: string) => void;

type DialogRequest =
  | { kind: 'confirm'; title: string; message: string; confirmLabel: string; danger?: boolean; action: () => Promise<void> }
  | { kind: 'prompt'; title: string; label: string; initial: string; placeholder?: string; action: (value: string) => Promise<void> };

function messageOf(error: unknown): string {
  if (error instanceof ApiClientError) return error.message;
  if (error instanceof Error) return error.message;
  return '操作失败';
}

function defaultCopyName(name: string): string {
  const suffix = ' 副本';
  const chars = Array.from(name);
  return `${chars.slice(0, 80 - Array.from(suffix).length).join('')}${suffix}`;
}

const DEVICE_LIFECYCLE_STATUS: Record<string, string> = {
  pending: '待注册',
  disabled: '已停用',
  revoked: '已吊销',
};

/** Activity windows, measured from last_seen_at (the sync heartbeat). */
const ONLINE_WINDOW_MS = 60 * 60 * 1000;
const STALE_WINDOW_MS = 6 * 60 * 60 * 1000;

/**
 * Persist only the folded groups, so a group created later still starts open
 * and "expand all" has an unambiguous representation.
 */
const COLLAPSED_GROUPS_STORAGE_KEY = 'edgedock.device-center.collapsed-groups';

/** Synthetic tree key for the "ungrouped" bucket, which has no group row in the API. */
const UNGROUPED_GROUP_KEY = '__ungrouped__';

function readCollapsedGroups(): Set<string> {
  try {
    const raw = window.localStorage.getItem(COLLAPSED_GROUPS_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((value): value is string => typeof value === 'string'));
  } catch {
    return new Set();
  }
}

function sameGroupSet(left: Set<string>, right: Set<string>): boolean {
  if (left.size !== right.size) return false;
  for (const id of left) {
    if (!right.has(id)) return false;
  }
  return true;
}

type DeviceActivity = { label: string; tone: string };

/**
 * Derive an active device's activity from its last sync. Admin edits bump
 * `updated_at`, so only `last_seen_at` separates "still reporting" from
 * "configured but stale".
 */
function deviceActivity(lastSeenAt: string | null, now: number): DeviceActivity {
  if (!lastSeenAt) return { label: '离线', tone: 'neutral' };
  const seen = new Date(lastSeenAt).getTime();
  if (Number.isNaN(seen)) return { label: '离线', tone: 'neutral' };
  const age = now - seen;
  if (age < ONLINE_WINDOW_MS) return { label: '在线', tone: 'ok' };
  if (age < STALE_WINDOW_MS) return { label: '存疑', tone: 'warn' };
  return { label: '离线', tone: 'neutral' };
}

/** Lifecycle states win; only `active` devices show a derived activity pill. */
function deviceStatus(device: Device, now: number): DeviceActivity {
  if (device.status !== 'active') {
    return {
      label: DEVICE_LIFECYCLE_STATUS[device.status] ?? device.status,
      tone: statusTone(device.status),
    };
  }
  return deviceActivity(device.last_seen_at, now);
}

const KEY_STATUS: Record<string, string> = {
  pending: '待激活',
  active: '已激活',
  revoked: '已吊销',
};

const MSG_STATUS: Record<string, string> = {
  active: '待确认',
  completed: '已完成',
  cancelled: '已取消',
};

const RUN_STATUS: Record<string, string> = {
  queued: '排队中',
  running: '执行中',
  succeeded: '成功',
  failed: '失败',
  cancelled: '已取消',
};

const SOURCE_LABEL: Record<string, string> = {
  log: '日志',
  'ip.reported': 'IP 上报',
  'boot.failed': 'Boot 失败',
  'boot.applied': 'Boot 成功',
  'device.online': '上线',
  'device.offline': '离线',
};

const IP_SOURCE_LABEL: Record<string, string> = {
  report: '显式上报',
  observed: '服务端观察',
};

const CHANNEL_LABEL: Record<string, string> = {
  telegram: 'Telegram',
  resend: 'Resend',
};

/** Map a status string to a visual tone class. */
function statusTone(status: string): string {
  if (status === 'revoked') return 'danger';
  if (status === 'pending') return 'warn';
  if (status === 'active' || status === 'completed') return 'ok';
  return 'neutral';
}

function runTone(status: string): string {
  if (status === 'succeeded') return 'ok';
  if (status === 'failed') return 'danger';
  if (status === 'cancelled') return 'neutral';
  return 'warn';
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

function formatTime(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('zh-CN', {
    month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

/** Coarse elapsed time for the device list; exact time stays in the tooltip. */
function formatAge(value: string | null | undefined, now: number): string {
  if (!value) return '从未同步';
  const seen = new Date(value).getTime();
  if (Number.isNaN(seen)) return '—';
  const age = Math.max(0, now - seen);
  if (age < 60 * 1000) return '刚刚';
  if (age < 60 * 60 * 1000) return `${Math.floor(age / (60 * 1000))} 分钟前`;
  if (age < 24 * 60 * 60 * 1000) return `${Math.floor(age / (60 * 60 * 1000))} 小时前`;
  return `${Math.floor(age / (24 * 60 * 60 * 1000))} 天前`;
}

type DetailTab = 'overview' | 'history' | 'console' | 'logs' | 'identify-logs' | 'install' | 'boot' | 'keys' | 'forwarding' | 'files' | 'messages' | 'scripts';

const TABS: { id: DetailTab; label: string; icon: typeof LayoutGrid }[] = [
  { id: 'overview', label: '概览', icon: LayoutGrid },
  { id: 'history', label: '同步历史', icon: History },
  { id: 'console', label: '控制台', icon: TerminalSquare },
  { id: 'logs', label: '实时日志', icon: Activity },
  { id: 'identify-logs', label: 'Identify 日志', icon: Fingerprint },
  { id: 'install', label: '安装', icon: FileText },
  { id: 'boot', label: 'Boot', icon: TerminalSquare },
  { id: 'keys', label: '密钥', icon: KeyRound },
  { id: 'forwarding', label: '转发', icon: Send },
  { id: 'files', label: '文件', icon: FileText },
  { id: 'messages', label: '消息', icon: MessageSquare },
  { id: 'scripts', label: '脚本', icon: Code2 },
];

const TAB_ALIASES: Record<string, DetailTab> = {
  overview: 'overview', '概览': 'overview',
  history: 'history', sync: 'history', ip_history: 'history', 'ip-history': 'history', '同步历史': 'history', 'IP 历史': 'history',
  console: 'console', shell: 'console', '控制台': 'console',
  logs: 'logs', logger: 'logs', '实时日志': 'logs',
  'identify-logs': 'identify-logs', identify: 'identify-logs', identify_logs: 'identify-logs', 'identify 日志': 'identify-logs', 'identify日志': 'identify-logs',
  install: 'install', '安装': 'install',
  boot: 'boot', boot_shell: 'boot',
  keys: 'keys', key: 'keys', '密钥': 'keys',
  forwarding: 'forwarding', '转发': 'forwarding',
  files: 'files', '文件': 'files',
  messages: 'messages', '消息': 'messages',
  scripts: 'scripts', script: 'scripts', '脚本': 'scripts',
};

function parseDeviceRoute(): { deviceID: string; tab: DetailTab } {
  const parts = window.location.pathname.split('/').filter(Boolean).map((part) => decodeURIComponent(part));
  if (parts[0] !== 'device-center') return { deviceID: '', tab: 'overview' };
  const deviceID = parts[1] ?? '';
  const tab = TAB_ALIASES[(parts[2] ?? '').toLowerCase()] ?? 'overview';
  return { deviceID, tab };
}

function devicePath(deviceID: string, tab: DetailTab): string {
  if (!deviceID) return '/device-center';
  return `/device-center/${encodeURIComponent(deviceID)}/${tab}`;
}

export function DeviceCenter({ notify }: { notify: Notify }) {
  const initialRoute = parseDeviceRoute();
  const [devices, setDevices] = useState<Device[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const [selectedId, setSelectedId] = useState<string>(initialRoute.deviceID);
  const [keys, setKeys] = useState<DeviceKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [keysLoading, setKeysLoading] = useState(false);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createFQDN, setCreateFQDN] = useState('');
  const [createIdentify, setCreateIdentify] = useState('');
  const [createNotes, setCreateNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editFQDN, setEditFQDN] = useState('');
  const [editIdentify, setEditIdentify] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editGroupID, setEditGroupID] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [copySource, setCopySource] = useState<Device | null>(null);
  const [copyName, setCopyName] = useState('');
  const [copyFQDN, setCopyFQDN] = useState('');
  const [copyGroupID, setCopyGroupID] = useState('');
  const [copying, setCopying] = useState(false);
  const [secret, setSecret] = useState<{ title: string; value: string } | null>(null);
  const [command, setCommand] = useState<InstallCommand | null>(null);
  const [commandFetch, setCommandFetch] = useState<'curl' | 'wget'>('curl');
  const [commandBusy, setCommandBusy] = useState(false);
  const [groups, setGroups] = useState<Group[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [query, setQuery] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(readCollapsedGroups);
  const [createGroupID, setCreateGroupID] = useState('');
  const [templateID, setTemplateID] = useState('');
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '', install: '', boot: '' });
  const [rules, setRules] = useState<ForwardingRule[]>([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [fileParent, setFileParent] = useState('');
  const [filePath, setFilePath] = useState<string[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [messages, setMessages] = useState<DeviceMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messageTitle, setMessageTitle] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [ipInfo, setIPInfo] = useState<IPInfo | null>(null);
  const [ipHistory, setIPHistory] = useState<IPHistoryItem[]>([]);
  const [ipHistoryNext, setIPHistoryNext] = useState<number | null>(null);
  const [ipHistoryLoading, setIPHistoryLoading] = useState(false);
  const [scripts, setScripts] = useState<DeviceScript[]>([]);
  const [scriptsLoading, setScriptsLoading] = useState(false);
  const [selectedScriptID, setSelectedScriptID] = useState('');
  const [scriptContent, setScriptContent] = useState('');
  const [scriptSavedContent, setScriptSavedContent] = useState('');
  const [scriptLanguage, setScriptLanguage] = useState('shell');
  const [scriptName, setScriptName] = useState('');
  const [scriptDescription, setScriptDescription] = useState('');
  const [scriptVersions, setScriptVersions] = useState<ScriptVersion[]>([]);
  const [scriptViewVersion, setScriptViewVersion] = useState<number | null>(null);
  const [scriptQuery, setScriptQuery] = useState('');
  const [scriptBusy, setScriptBusy] = useState(false);
  const [scriptModalOpen, setScriptModalOpen] = useState(false);
  const [scriptSettingsOpen, setScriptSettingsOpen] = useState(false);
  const [scriptSettings, setScriptSettings] = useState({ name: '', description: '', language: 'shell' });
  const [scriptRunsOpen, setScriptRunsOpen] = useState(false);
  const [scriptURL, setScriptURL] = useState('');
  const scriptAutoOpenRef = useRef('');
  const scriptsDeviceRef = useRef('');
  const [installVersions, setInstallVersions] = useState<InstallVersion[]>([]);
  const [installScript, setInstallScript] = useState('');
  const [installVersion, setInstallVersion] = useState('');
  const [installSHA, setInstallSHA] = useState('');
  const [bootVersions, setBootVersions] = useState<BootVersion[]>([]);
  const [bootScript, setBootScript] = useState('');
  const [bootSettings, setBootSettings] = useState('{}');
  const [bootVersion, setBootVersion] = useState('');
  const [bootSHA, setBootSHA] = useState('');
  const [bootRuns, setBootRuns] = useState<BootRun[]>([]);
  const [assetBusy, setAssetBusy] = useState(false);
  const [scriptRuns, setScriptRuns] = useState<ScriptRun[]>([]);
  const [scriptRunsLoading, setScriptRunsLoading] = useState(false);
  const [selectedRun, setSelectedRun] = useState<ScriptRun | null>(null);
  const [newScript, setNewScript] = useState({ name: '', description: '', language: 'shell', content: '#!/bin/sh\n' });
  const [ruleOpen, setRuleOpen] = useState(false);
  const [ruleForm, setRuleForm] = useState({ name: '', source: 'log', channel: 'telegram', levels: 'error', regex: '' });
  const [tab, setTab] = useState<DetailTab>(initialRoute.tab);
  const [dialog, setDialog] = useState<DialogRequest | null>(null);
  const [dialogBusy, setDialogBusy] = useState(false);

  async function submitDialog(value?: string) {
    if (!dialog || dialogBusy) return;
    setDialogBusy(true);
    try {
      if (dialog.kind === 'prompt') await dialog.action(value ?? '');
      else await dialog.action();
      setDialog(null);
    } catch (cause) {
      notify('error', messageOf(cause));
    } finally {
      setDialogBusy(false);
    }
  }

  function selectDevice(deviceID: string, nextTab: DetailTab = tab) {
    setSelectedId(deviceID);
    setTab(nextTab);
    const path = devicePath(deviceID, nextTab);
    if (window.location.pathname !== path) window.history.pushState({}, '', path);
  }

  function selectTab(nextTab: DetailTab) {
    setTab(nextTab);
    if (selectedId) {
      const path = devicePath(selectedId, nextTab);
      if (window.location.pathname !== path) window.history.pushState({}, '', path);
    }
  }

  useEffect(() => {
    const onPop = () => {
      const route = parseDeviceRoute();
      setSelectedId(route.deviceID);
      setTab(route.tab);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    const path = devicePath(selectedId, tab);
    if (window.location.pathname !== path) window.history.replaceState({}, '', path);
  }, [selectedId, tab]);

  const selected = devices.find((device) => device.id === selectedId) ?? null;
  const selectedScript = scripts.find((script) => script.id === selectedScriptID) ?? null;
  const scriptActiveVersion = selectedScript?.active_version ?? null;
  const scriptDirty = scriptContent !== scriptSavedContent;
  const filteredScripts = useMemo(() => {
    const text = scriptQuery.trim().toLowerCase();
    if (!text) return scripts;
    return scripts.filter((script) => script.name.toLowerCase().includes(text)
      || script.description.toLowerCase().includes(text)
      || script.language.toLowerCase().includes(text));
  }, [scripts, scriptQuery]);
  const visibleDevices = useMemo(() => {
    const text = query.trim().toLowerCase();
    return devices.filter((device) => {
      if (!text) return true;
      return device.name.toLowerCase().includes(text)
        || (device.fqdn ?? '').toLowerCase().includes(text);
    });
  }, [devices, query]);

  const loadDevices = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api<{ devices: Device[] }>('/api/devices');
      setDevices(data.devices);
      setSelectedId((current) => (
        data.devices.some((device) => device.id === current) ? current : ''
      ));
      setError('');
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadKeys = useCallback(async (deviceId: string) => {
    if (!deviceId) return;
    setKeysLoading(true);
    try {
      const data = await api<{ keys: DeviceKey[] }>(`/api/devices/${encodeURIComponent(deviceId)}/keys`);
      setKeys(data.keys);
    } catch (cause) {
      notify('error', messageOf(cause));
    } finally {
      setKeysLoading(false);
    }
  }, [notify]);

  useEffect(() => { void loadDevices(); }, [loadDevices]);

  // 记住每个分组的折叠状态；存储不可用时只失去记忆能力，不影响展开/收起。
  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSED_GROUPS_STORAGE_KEY, JSON.stringify([...collapsedGroups].sort()));
    } catch {
      // 隐私模式或配额受限。
    }
  }, [collapsedGroups]);

  // 同一账号在多个标签页打开时，保持目录树状态一致。
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== COLLAPSED_GROUPS_STORAGE_KEY || event.newValue === event.oldValue) return;
      const next = readCollapsedGroups();
      setCollapsedGroups((current) => (sameGroupSet(current, next) ? current : next));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Re-evaluate activity pills without waiting for a manual refresh.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    void api<{ groups: Group[] }>('/api/device-groups').then((data) => setGroups(data.groups)).catch(() => undefined);
    void api<{ templates: Template[] }>('/api/device-templates').then((data) => setTemplates(data.templates)).catch(() => undefined);
  }, []);

  const loadRules = useCallback(async (deviceId: string) => {
    if (!deviceId) return;
    setRulesLoading(true);
    try {
      const data = await api<{ rules: ForwardingRule[] }>(`/api/devices/${encodeURIComponent(deviceId)}/forwarding`);
      setRules(data.rules);
    } catch (cause) {
      notify('error', messageOf(cause));
    } finally {
      setRulesLoading(false);
    }
  }, [notify]);

  useEffect(() => { if (selectedId) void loadKeys(selectedId); }, [selectedId, loadKeys]);
  useEffect(() => { if (selectedId) void loadRules(selectedId); }, [selectedId, loadRules]);

  const loadFiles = useCallback(async (deviceId: string, parentID: string) => {
    if (!deviceId) return;
    setFilesLoading(true);
    try {
      const query = parentID ? `?parent_id=${encodeURIComponent(parentID)}` : '';
      const data = await api<{ items: FileEntry[] }>(`/api/devices/${encodeURIComponent(deviceId)}/files${query}`);
      setFiles(data.items);
      setFileParent(parentID);
    } catch (cause) {
      notify('error', messageOf(cause));
    } finally {
      setFilesLoading(false);
    }
  }, [notify]);

  const loadMessages = useCallback(async (deviceId: string) => {
    if (!deviceId) return;
    setMessagesLoading(true);
    try {
      const data = await api<{ items: DeviceMessage[] }>(`/api/devices/${encodeURIComponent(deviceId)}/messages`);
      setMessages(data.items);
    } catch (cause) {
      notify('error', messageOf(cause));
    } finally {
      setMessagesLoading(false);
    }
  }, [notify]);

  const loadIP = useCallback(async (deviceId: string) => {
    try {
      const data = await api<IPInfo>(`/api/devices/${encodeURIComponent(deviceId)}/ip`);
      setIPInfo(data);
    } catch {
      setIPInfo(null);
    }
  }, []);

  const loadIPHistory = useCallback(async (deviceId: string, before: number | null = null) => {
    if (!deviceId) return;
    setIPHistoryLoading(true);
    if (before === null) {
      setIPHistory([]);
      setIPHistoryNext(null);
    }
    try {
      const query = new URLSearchParams({ limit: '50' });
      if (before !== null) query.set('before', String(before));
      const data = await api<{ items: IPHistoryItem[]; nextCursor: number | null }>(
        `/api/devices/${encodeURIComponent(deviceId)}/ip-history?${query.toString()}`,
      );
      setIPHistory((current) => (before === null ? data.items : [...current, ...data.items]));
      setIPHistoryNext(data.nextCursor ?? null);
    } catch (cause) {
      notify('error', messageOf(cause));
    } finally {
      setIPHistoryLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    if (tab === 'history' && selectedId) void loadIPHistory(selectedId);
  }, [tab, selectedId, loadIPHistory]);

  const loadScripts = useCallback(async (deviceId: string) => {
    if (!deviceId) return [];
    setScriptsLoading(true);
    try {
      const data = await api<{ scripts: DeviceScript[] }>(`/api/devices/${encodeURIComponent(deviceId)}/scripts`);
      scriptsDeviceRef.current = deviceId;
      setScripts(data.scripts);
      return data.scripts;
    } catch (cause) {
      notify('error', messageOf(cause));
      return [];
    } finally {
      setScriptsLoading(false);
    }
  }, [notify]);

  const loadInstallAssets = useCallback(async (deviceId: string) => {
    try {
      const data = await api<{ items: InstallVersion[] }>(`/api/devices/${encodeURIComponent(deviceId)}/install`);
      setInstallVersions(data.items);
      if (data.items.length > 0) {
        const asset = await api<InstallAsset>(`/api/devices/${encodeURIComponent(deviceId)}/install/${encodeURIComponent(data.items[0].version)}`);
        setInstallScript(asset.script);
        setInstallVersion(asset.version);
        setInstallSHA(asset.sha256);
      } else {
        setInstallScript('');
        setInstallVersion('');
        setInstallSHA('');
      }
    } catch (cause) { notify('error', messageOf(cause)); }
  }, [notify]);

  const loadBootAssets = useCallback(async (deviceId: string) => {
    try {
      const [data, runs] = await Promise.all([
        api<{ items: BootVersion[] }>(`/api/devices/${encodeURIComponent(deviceId)}/boot`),
        api<{ items: BootRun[] }>(`/api/devices/${encodeURIComponent(deviceId)}/boot/runs`),
      ]);
      setBootVersions(data.items);
      setBootRuns(runs.items);
      if (data.items.length > 0) {
        const asset = await api<BootAsset>(`/api/devices/${encodeURIComponent(deviceId)}/boot/${encodeURIComponent(data.items[0].version)}`);
        setBootScript(asset.script);
        setBootVersion(asset.version);
        setBootSHA(asset.sha256);
        setBootSettings(JSON.stringify(asset.settings ?? {}, null, 2));
      } else {
        setBootScript('');
        setBootVersion('');
        setBootSHA('');
        setBootSettings('{}');
      }
    } catch (cause) { notify('error', messageOf(cause)); }
  }, [notify]);

  function clearScriptView() {
    setSelectedScriptID('');
    setScriptName('');
    setScriptDescription('');
    setScriptContent('');
    setScriptSavedContent('');
    setScriptVersions([]);
    setScriptViewVersion(null);
    setScriptRuns([]);
    setSelectedRun(null);
    setScriptURL('');
    setScriptRunsOpen(false);
    setScriptSettingsOpen(false);
  }

  useEffect(() => {
    if (!selectedId) return;
    setFilePath([]);
    setScriptQuery('');
    setScripts([]);
    scriptsDeviceRef.current = '';
    scriptAutoOpenRef.current = '';
    clearScriptView();
    void loadFiles(selectedId, '');
    void loadMessages(selectedId);
    void loadIP(selectedId);
    void loadScripts(selectedId);
    void loadInstallAssets(selectedId);
    void loadBootAssets(selectedId);
  }, [selectedId, loadFiles, loadMessages, loadIP, loadScripts, loadInstallAssets, loadBootAssets]);

  useEffect(() => {
    if (!selectedId || selectedScriptID || scripts.length === 0) return;
    if (scriptsDeviceRef.current !== selectedId) return;
    if (scriptAutoOpenRef.current === selectedId) return;
    scriptAutoOpenRef.current = selectedId;
    void openScript(scripts[0]);
  }, [selectedId, scripts, selectedScriptID]);

  function openCopy(device: Device) {
    setCopySource(device);
    setCopyName(defaultCopyName(device.name));
    setCopyFQDN('');
    setCopyGroupID(device.group_id ?? '');
  }

  async function copyDevice() {
    if (!copySource) return;
    setCopying(true);
    try {
      const payload: Record<string, string> = { name: copyName.trim(), group_id: copyGroupID };
      if (copyFQDN.trim()) payload.fqdn = copyFQDN.trim();
      const data = await api<{ device: Device; copied: CopyStats }>(
        `/api/devices/${encodeURIComponent(copySource.id)}/copy`,
        { method: 'POST', body: jsonBody(payload) },
      );
      setDevices((current) => [data.device, ...current]);
      setCopySource(null);
      selectDevice(data.device.id, 'overview');
      notify('success', `设备已复制：${data.copied.scripts} 个脚本、${data.copied.install_versions} 个 install 版本、${data.copied.boot_versions} 个 boot 版本、${data.copied.forward_rules} 条转发规则`);
    } catch (cause) {
      notify('error', messageOf(cause));
    } finally {
      setCopying(false);
    }
  }

  async function createDevice() {
    setCreating(true);
    try {
      const payload: Record<string, string> = { name: createName };
      if (createFQDN.trim()) payload.fqdn = createFQDN.trim();
      if (createIdentify.trim()) payload.identify = createIdentify.trim();
      if (createNotes.trim()) payload.notes = createNotes.trim();
      if (createGroupID) payload.group_id = createGroupID;
      const data = await api<{ device: Device }>('/api/devices', { method: 'POST', body: jsonBody(payload) });
      setDevices((current) => [data.device, ...current]);
      selectDevice(data.device.id, 'overview');
      setCreateOpen(false);
      setCreateName('');
      setCreateFQDN('');
      setCreateIdentify('');
      setCreateNotes('');
      setCreateGroupID('');
      notify('success', `设备已创建${data.device.identify ? `，Identify：${data.device.identify}` : ''}`);
    } catch (cause) {
      notify('error', messageOf(cause));
    } finally {
      setCreating(false);
    }
  }

  function openEdit(device: Device) {
    setEditName(device.name);
    setEditFQDN(device.fqdn ?? '');
    setEditIdentify(device.identify ?? '');
    setEditNotes(device.notes ?? '');
    setEditGroupID(device.group_id ?? '');
    setEditOpen(true);
  }

  function generateIdentify() {
    const alphabet = 'abcdefghijkmnpqrstuvwxyz23456789';
    const bytes = new Uint8Array(8);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (value) => alphabet[value % alphabet.length]).join('');
  }

  async function saveDevice() {
    if (!selected) return;
    setSavingEdit(true);
    try {
      const payload: Record<string, string> = {
        name: editName.trim(),
        notes: editNotes.trim(),
        fqdn: editFQDN.trim(),
        identify: editIdentify.trim(),
        group_id: editGroupID,
      };
      const data = await api<{ device: Device }>(`/api/devices/${encodeURIComponent(selected.id)}`, {
        method: 'PATCH', body: jsonBody(payload),
      });
      setDevices((current) => current.map((device) => (device.id === data.device.id ? data.device : device)));
      setEditOpen(false);
      notify('success', '设备已更新');
    } catch (cause) {
      notify('error', messageOf(cause));
    } finally {
      setSavingEdit(false);
    }
  }

  async function issue(mode: 'proof' | 'preprovisioned') {
    if (!selected) return;
    try {
      const data = await api<Credential>(`/api/devices/${encodeURIComponent(selected.id)}/keys`, {
        method: 'POST', body: jsonBody({ mode }),
      });
      await loadKeys(selected.id);
      const value = mode === 'proof' ? (data.token ?? '') : (data.key ?? '');
      setSecret({ title: mode === 'proof' ? 'proof 注册 token' : '预置设备 key', value });
    } catch (cause) {
      notify('error', messageOf(cause));
    }
  }

  async function makeCommand(mode: 'proof' | 'preprovisioned') {
    if (!selected) return;
    setCommandBusy(true);
    try {
      const data = await api<InstallCommand>(`/api/devices/${encodeURIComponent(selected.id)}/install-command`, {
        method: 'POST', body: jsonBody({ mode, fetch: commandFetch }),
      });
      const hasBothCommands = Boolean(data.commands?.curl && data.commands?.wget);
      setCommandFetch(hasBothCommands ? (data.fetch ?? commandFetch) : 'curl');
      setCommand(data);
    } catch (cause) {
      notify('error', messageOf(cause));
    } finally {
      setCommandBusy(false);
    }
  }

  function revoke(keyID: string) {
    if (!selected) return;
    const deviceID = selected.id;
    setDialog({
      kind: 'confirm',
      title: '吊销密钥',
      message: '吊销后该 key 立即失效，确定继续？',
      confirmLabel: '吊销',
      danger: true,
      action: async () => {
        await api(`/api/devices/${encodeURIComponent(deviceID)}/keys/${encodeURIComponent(keyID)}`, { method: 'DELETE' });
        await loadKeys(deviceID);
        notify('success', 'key 已吊销');
      },
    });
  }

  async function createRule() {
    if (!selected) return;
    try {
      await api(`/api/devices/${encodeURIComponent(selected.id)}/forwarding`, {
        method: 'POST',
        body: jsonBody({
          name: ruleForm.name,
          source: ruleForm.source,
          channel: ruleForm.channel,
          filter: {
            levels: ruleForm.levels ? ruleForm.levels.split(',').map((item) => item.trim()).filter(Boolean) : [],
            message_regex: ruleForm.regex,
          },
        }),
      });
      setRuleOpen(false);
      setRuleForm({ name: '', source: 'log', channel: 'telegram', levels: 'error', regex: '' });
      await loadRules(selected.id);
      notify('success', '转发规则已创建');
    } catch (cause) {
      notify('error', messageOf(cause));
    }
  }

  async function deleteRule(ruleID: string) {
    if (!selected) return;
    try {
      await api(`/api/devices/${encodeURIComponent(selected.id)}/forwarding/${encodeURIComponent(ruleID)}`, { method: 'DELETE' });
      await loadRules(selected.id);
      notify('success', '转发规则已删除');
    } catch (cause) {
      notify('error', messageOf(cause));
    }
  }

  async function uploadFile(file: File) {
    if (!selected) return;
    const form = new FormData();
    form.append('file', file);
    if (fileParent) form.append('parent_id', fileParent);
    try {
      await api(`/api/devices/${encodeURIComponent(selected.id)}/files/upload`, { method: 'POST', body: form });
      await loadFiles(selected.id, fileParent);
      notify('success', '文件已上传');
    } catch (cause) {
      notify('error', messageOf(cause));
    }
  }

  function createFolder() {
    if (!selected) return;
    const deviceID = selected.id;
    setDialog({
      kind: 'prompt',
      title: '新建文件夹',
      label: '文件夹名称',
      initial: '',
      placeholder: '例如：logs',
      action: async (name) => {
        await api(`/api/devices/${encodeURIComponent(deviceID)}/files/folders`, {
          method: 'POST', body: jsonBody({ name, parent_id: fileParent || null }),
        });
        await loadFiles(deviceID, fileParent);
      },
    });
  }

  function openFolder(entry: FileEntry) {
    if (!selected) return;
    setFilePath((current) => [...current, entry.name]);
    void loadFiles(selected.id, entry.id);
  }

  function backToRoot() {
    if (!selected) return;
    setFilePath([]);
    void loadFiles(selected.id, '');
  }

  function deleteFile(entryID: string) {
    if (!selected) return;
    const deviceID = selected.id;
    setDialog({
      kind: 'confirm',
      title: '删除文件',
      message: '删除该文件/文件夹？此操作不可撤销。',
      confirmLabel: '删除',
      danger: true,
      action: async () => {
        await api(`/api/devices/${encodeURIComponent(deviceID)}/files/${encodeURIComponent(entryID)}`, { method: 'DELETE' });
        await loadFiles(deviceID, fileParent);
      },
    });
  }

  async function createMessage() {
    if (!selected) return;
    try {
      await api(`/api/devices/${encodeURIComponent(selected.id)}/messages`, {
        method: 'POST', body: jsonBody({ title: messageTitle, content: messageContent }),
      });
      setMessageTitle('');
      setMessageContent('');
      await loadMessages(selected.id);
      notify('success', '消息已创建');
    } catch (cause) {
      notify('error', messageOf(cause));
    }
  }

  async function cancelMessage(messageID: string) {
    if (!selected) return;
    try {
      await api(`/api/devices/${encodeURIComponent(selected.id)}/messages/${encodeURIComponent(messageID)}/cancel`, { method: 'POST' });
      await loadMessages(selected.id);
    } catch (cause) {
      notify('error', messageOf(cause));
    }
  }

  async function loadScriptRuns(deviceId: string, scriptID: string) {
    setScriptRunsLoading(true);
    try {
      const data = await api<{ items: ScriptRun[] }>(`/api/devices/${encodeURIComponent(deviceId)}/scripts/${encodeURIComponent(scriptID)}/runs`);
      setScriptRuns(data.items);
      return data.items;
    } catch (cause) {
      notify('error', messageOf(cause));
      return [];
    } finally {
      setScriptRunsLoading(false);
    }
  }

  async function runScriptNow() {
    if (!selected || !selectedScriptID) return;
    setScriptBusy(true);
    try {
      const data = await api<{ run: ScriptRun }>(`/api/devices/${encodeURIComponent(selected.id)}/scripts/${encodeURIComponent(selectedScriptID)}/run`, { method: 'POST', body: jsonBody({ version: scriptViewVersion ?? undefined }) });
      const items = await loadScriptRuns(selected.id, selectedScriptID);
      setSelectedRun(items.find((run) => run.id === data.run.id) ?? data.run);
      notify('success', '脚本已下发；等待设备拉取执行');
    } catch (cause) {
      notify('error', messageOf(cause));
    } finally {
      setScriptBusy(false);
    }
  }

  async function refreshScriptRuns() {
    if (!selected || !selectedScriptID) return;
    const items = await loadScriptRuns(selected.id, selectedScriptID);
    setSelectedRun((current) => (current && items.some((run) => run.id === current.id) ? current : items[0] ?? null));
  }

  async function copyScriptURL() {
    if (!scriptURL) return;
    try {
      await navigator.clipboard.writeText(scriptURL);
      notify('success', '脚本 URL 已复制');
    } catch {
      notify('error', '无法复制脚本 URL');
    }
  }

  async function loadScriptVersions(deviceId: string, scriptID: string) {
    const data = await api<{ versions: ScriptVersion[] }>(`/api/devices/${encodeURIComponent(deviceId)}/scripts/${encodeURIComponent(scriptID)}/versions`);
    setScriptVersions(data.versions);
    return data.versions;
  }

  async function openScript(script: DeviceScript, version?: number) {
    if (!selected) return;
    const deviceId = selected.id;
    const suffix = version ? `?version=${version}` : '';
    setScriptBusy(true);
    try {
      const data = await api<{ script: DeviceScript; version: ScriptVersion; url?: string }>(
        `/api/devices/${encodeURIComponent(deviceId)}/scripts/${encodeURIComponent(script.id)}${suffix}`);
      setSelectedScriptID(data.script.id);
      setScriptName(data.script.name);
      setScriptLanguage(data.script.language);
      setScriptDescription(data.script.description);
      setScriptContent(data.version.content ?? '');
      setScriptSavedContent(data.version.content ?? '');
      setScriptViewVersion(data.version.version);
      setScriptURL(data.url ?? '');
      const [versions, runs] = await Promise.all([
        loadScriptVersions(deviceId, data.script.id),
        loadScriptRuns(deviceId, data.script.id),
      ]);
      setScriptVersions(versions);
      setScriptRuns(runs);
      setSelectedRun(runs[0] ?? null);
    } catch (cause) {
      notify('error', messageOf(cause));
    } finally {
      setScriptBusy(false);
    }
  }

  async function saveScriptVersion(activate: boolean) {
    if (!selected || !selectedScriptID) return;
    setScriptBusy(true);
    try {
      const data = await api<{ version: ScriptVersion }>(
        `/api/devices/${encodeURIComponent(selected.id)}/scripts/${encodeURIComponent(selectedScriptID)}/versions`,
        { method: 'POST', body: jsonBody({ content: scriptContent, activate }) });
      const list = await loadScripts(selected.id);
      const current = list.find((item) => item.id === selectedScriptID);
      if (activate) {
        if (current) await openScript(current);
      } else if (current) {
        await openScript(current, data.version.version);
      } else {
        setScriptSavedContent(scriptContent);
        setScriptViewVersion(data.version.version);
        await loadScriptVersions(selected.id, selectedScriptID);
      }
      notify('success', activate ? '新版本已保存并激活' : '新版本已保存');
    } catch (cause) {
      notify('error', messageOf(cause));
    } finally {
      setScriptBusy(false);
    }
  }

  async function activateScriptVersion() {
    if (!selected || !selectedScriptID || !scriptViewVersion) return;
    setScriptBusy(true);
    try {
      await api(`/api/devices/${encodeURIComponent(selected.id)}/scripts/${encodeURIComponent(selectedScriptID)}/activate`,
        { method: 'POST', body: jsonBody({ version: scriptViewVersion }) });
      await loadScripts(selected.id);
      notify('success', `已激活 v${scriptViewVersion}`);
    } catch (cause) {
      notify('error', messageOf(cause));
    } finally {
      setScriptBusy(false);
    }
  }

  async function updateScriptSettings() {
    if (!selected || !selectedScriptID) return;
    setScriptBusy(true);
    try {
      const data = await api<{ script: DeviceScript }>(
        `/api/devices/${encodeURIComponent(selected.id)}/scripts/${encodeURIComponent(selectedScriptID)}`,
        { method: 'PATCH', body: jsonBody(scriptSettings) });
      setScriptSettingsOpen(false);
      await loadScripts(selected.id);
      await openScript(data.script);
      notify('success', '脚本设置已更新');
    } catch (cause) {
      notify('error', messageOf(cause));
    } finally {
      setScriptBusy(false);
    }
  }

  function deleteScript() {
    if (!selected || !selectedScriptID) return;
    const deviceID = selected.id;
    const scriptID = selectedScriptID;
    setDialog({
      kind: 'confirm',
      title: '删除脚本',
      message: '删除脚本及全部版本？此操作不可撤销。',
      confirmLabel: '删除',
      danger: true,
      action: async () => {
        await api(`/api/devices/${encodeURIComponent(deviceID)}/scripts/${encodeURIComponent(scriptID)}`, { method: 'DELETE' });
        const list = await loadScripts(deviceID);
        clearScriptView();
        if (list.length > 0) await openScript(list[0]);
        notify('success', '脚本已删除');
      },
    });
  }

  async function createScript() {
    if (!selected) return;
    setScriptBusy(true);
    try {
      const data = await api<{ script: DeviceScript }>(`/api/devices/${encodeURIComponent(selected.id)}/scripts`, {
        method: 'POST',
        body: jsonBody({
          name: newScript.name, description: newScript.description,
          language: newScript.language, content: newScript.content,
        }),
      });
      setScriptModalOpen(false);
      setNewScript({ name: '', description: '', language: 'shell', content: '#!/bin/sh\n' });
      await loadScripts(selected.id);
      await openScript(data.script);
      notify('success', '脚本已创建');
    } catch (cause) {
      notify('error', messageOf(cause));
    } finally {
      setScriptBusy(false);
    }
  }

  async function openInstallVersion(version: string) {
    if (!selected) return;
    try {
      const asset = await api<InstallAsset>(`/api/devices/${encodeURIComponent(selected.id)}/install/${encodeURIComponent(version)}`);
      setInstallScript(asset.script);
      setInstallVersion(asset.version);
      setInstallSHA(asset.sha256);
    } catch (cause) { notify('error', messageOf(cause)); }
  }

  async function saveInstall() {
    if (!selected || !installScript.trim()) return;
    setAssetBusy(true);
    try {
      await api(`/api/devices/${encodeURIComponent(selected.id)}/install`, { method: 'POST', body: jsonBody({ version: installVersion, script: installScript }) });
      await loadInstallAssets(selected.id);
      notify('success', 'install 版本已保存并激活');
    } catch (cause) { notify('error', messageOf(cause)); }
    finally { setAssetBusy(false); }
  }

  async function openBootVersion(version: string) {
    if (!selected) return;
    try {
      const asset = await api<BootAsset>(`/api/devices/${encodeURIComponent(selected.id)}/boot/${encodeURIComponent(version)}`);
      setBootScript(asset.script);
      setBootVersion(asset.version);
      setBootSHA(asset.sha256);
      setBootSettings(JSON.stringify(asset.settings ?? {}, null, 2));
    } catch (cause) { notify('error', messageOf(cause)); }
  }

  async function saveBoot() {
    if (!selected || !bootScript.trim()) return;
    setAssetBusy(true);
    try {
      let settings: unknown = {};
      try { settings = JSON.parse(bootSettings || '{}'); } catch { notify('error', 'settings 不是合法 JSON'); return; }
      await api(`/api/devices/${encodeURIComponent(selected.id)}/boot`, { method: 'POST', body: jsonBody({ version: bootVersion, script: bootScript, settings }) });
      await loadBootAssets(selected.id);
      notify('success', 'boot 版本已保存并激活');
    } catch (cause) { notify('error', messageOf(cause)); }
    finally { setAssetBusy(false); }
  }

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      notify('success', '已复制');
    } catch {
      notify('error', '无法复制，请手动选择');
    }
  }

  function createGroup(parentID?: string) {
    setDialog({
      kind: 'prompt',
      title: parentID ? '新建子分组' : '新建分组',
      label: '分组名称',
      initial: '',
      placeholder: '例如：华东节点',
      action: async (name) => {
        const payload: Record<string, string> = { name };
        if (parentID) payload.parent_id = parentID;
        const data = await api<{ group: Group }>('/api/device-groups', { method: 'POST', body: jsonBody(payload) });
        setGroups((current) => [...current, data.group]);
        // 新建分组默认展开；如果父分组之前被折叠，一并展开以露出新分组。
        setCollapsedGroups((current) => {
          if (!current.has(data.group.id) && (!parentID || !current.has(parentID))) return current;
          const next = new Set(current);
          next.delete(data.group.id);
          if (parentID) next.delete(parentID);
          return next;
        });
        notify('success', '分组已创建');
      },
    });
  }

  function renameGroup(group: Group) {
    setDialog({
      kind: 'prompt',
      title: '重命名分组',
      label: '分组名称',
      initial: group.name,
      action: async (name) => {
        if (name === group.name) return;
        await api(`/api/device-groups/${encodeURIComponent(group.id)}`, { method: 'PATCH', body: jsonBody({ name }) });
        setGroups((current) => current.map((item) => (item.id === group.id ? { ...item, name } : item)));
        notify('success', '分组已重命名');
      },
    });
  }

  function deleteGroup(group: Group) {
    setDialog({
      kind: 'confirm',
      title: '删除分组',
      message: `删除分组“${group.name}”？子分组会一并删除，组内设备将变为未分组。`,
      confirmLabel: '删除',
      danger: true,
      action: async () => {
        await api(`/api/device-groups/${encodeURIComponent(group.id)}`, { method: 'DELETE' });
        setGroups((current) => current.filter((item) => item.id !== group.id));
        await loadDevices();
        notify('success', '分组已删除');
      },
    });
  }

  async function createTemplate() {
    try {
      const data = await api<{ template: Template }>('/api/device-templates', {
        method: 'POST',
        body: jsonBody({
          name: templateForm.name,
          description: templateForm.description,
          install_script: templateForm.install,
          boot_script: templateForm.boot,
        }),
      });
      setTemplates((current) => [data.template, ...current]);
      setTemplateOpen(false);
      setTemplateForm({ name: '', description: '', install: '', boot: '' });
      notify('success', '模板已创建');
    } catch (cause) {
      notify('error', messageOf(cause));
    }
  }

  function applyTemplate() {
    if (!selected || !templateID) return;
    const deviceID = selected.id;
    setDialog({
      kind: 'confirm',
      title: '应用设备模板',
      message: '应用模板会新增 install/boot 版本，确定继续？',
      confirmLabel: '应用',
      action: async () => {
        await api(`/api/device-templates/${encodeURIComponent(templateID)}/apply`, {
          method: 'POST', body: jsonBody({ device_id: deviceID }),
        });
        notify('success', '模板已应用');
      },
    });
  }

  function Empty({ icon: Icon, title, hint }: { icon: typeof FileText; title: string; hint?: string }) {
    return (
      <div className="empty-state">
        <span className="empty-icon"><Icon size={22} /></span>
        <strong>{title}</strong>
        {hint && <p>{hint}</p>}
      </div>
    );
  }

  const groupByID = useMemo(() => new Map(groups.map((group) => [group.id, group] as const)), [groups]);

  const groupChildren = useMemo(() => {
    const map = new Map<string, Group[]>();
    for (const group of groups) {
      const key = group.parent_id ?? '';
      const list = map.get(key) ?? [];
      list.push(group);
      map.set(key, list);
    }
    return map;
  }, [groups]);

  const deviceByGroup = useMemo(() => {
    const map = new Map<string, Device[]>();
    for (const device of devices) {
      const key = device.group_id ?? '';
      const list = map.get(key) ?? [];
      list.push(device);
      map.set(key, list);
    }
    return map;
  }, [devices]);

  // 分组下拉使用「父 / 子」完整路径：同名子分组（例如两个 123）不会混淆。
  const { groupPathMap, groupOptions } = useMemo(() => {
    const paths = new Map<string, string>();
    const options: { id: string; name: string; path: string }[] = [];
    const visited = new Set<string>();
    const walk = (parentID: string, prefix: string) => {
      for (const group of groupChildren.get(parentID) ?? []) {
        if (visited.has(group.id)) continue;
        visited.add(group.id);
        const path = prefix ? `${prefix} / ${group.name}` : group.name;
        paths.set(group.id, path);
        options.push({ id: group.id, name: group.name, path });
        walk(group.id, path);
      }
    };
    walk('', '');
    // 父分组丢失的孤儿节点仍要可选，避免数据异常时无法在 UI 里调整。
    for (const group of groups) {
      if (visited.has(group.id)) continue;
      paths.set(group.id, group.name);
      options.push({ id: group.id, name: group.name, path: group.name });
    }
    return { groupPathMap: paths, groupOptions: options };
  }, [groupChildren, groups]);

  const rootGroups = groupChildren.get('') ?? [];
  const ungroupedDevices = deviceByGroup.get('') ?? [];
  const allGroupIDs = useMemo(() => {
    const ids = groups.map((group) => group.id);
    if (ungroupedDevices.length > 0) ids.push(UNGROUPED_GROUP_KEY);
    return ids;
  }, [groups, ungroupedDevices.length]);

  const selectedGroupChain = useMemo(() => {
    const chain: Group[] = [];
    let current = selected?.group_id ?? null;
    while (current) {
      const group = groupByID.get(current);
      if (!group) break;
      chain.unshift(group);
      current = group.parent_id;
    }
    return chain;
  }, [selected, groupByID]);

  function groupPathOf(device: Device): string {
    if (!device.group_id) return '未分组';
    return groupPathMap.get(device.group_id) ?? '未分组';
  }

  function countDevicesUnder(groupID: string): number {
    let total = (deviceByGroup.get(groupID) ?? []).length;
    for (const child of (groupChildren.get(groupID) ?? [])) total += countDevicesUnder(child.id);
    return total;
  }

  function toggleGroup(groupID: string) {
    setCollapsedGroups((current) => {
      const next = new Set(current);
      if (next.has(groupID)) next.delete(groupID);
      else next.add(groupID);
      return next;
    });
  }

  function expandAllGroups() {
    setCollapsedGroups((current) => (current.size === 0 ? current : new Set()));
  }

  function collapseAllGroups() {
    setCollapsedGroups(new Set(allGroupIDs));
  }

  function goBackToList() {
    setSelectedId('');
    setTab('overview');
    if (window.location.pathname !== '/device-center') window.history.pushState({}, '', '/device-center');
  }

  function renderDeviceRow(device: Device, depth: number, showGroup = false) {
    const ip = device.last_ip;
    const status = deviceStatus(device, now);
    return (
      <div
        className="device-tree-row device-row"
        key={device.id}
        style={{ '--depth': depth } as CSSProperties}
        role="button"
        tabIndex={0}
        onClick={() => selectDevice(device.id, 'overview')}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            selectDevice(device.id, 'overview');
          }
        }}
      >
        <span className="device-tree-name">
          <span className="device-tree-toggle-spacer" />
          <Server size={16} />
          <span className="device-title">
            <strong>{device.name}</strong>
            <small>{showGroup ? groupPathOf(device) : (device.fqdn ?? '无域名 · 仅 IP')}</small>
          </span>
        </span>
        <span className="device-tree-ip">
          {ip ? (
            <>
              <span title={ip}>{ip}</span>
              <button
                type="button"
                className="icon-button"
                title="复制 IP"
                aria-label={`复制 IP ${ip}`}
                onClick={(event) => { event.stopPropagation(); void copy(ip); }}
              >
                <Copy size={13} />
              </button>
            </>
          ) : <span className="device-tree-muted">—</span>}
        </span>
        <span className="device-tree-col">
          <span className={`status-pill ${status.tone}`} title={device.last_seen_at ? `最近同步 ${formatTime(device.last_seen_at)}` : '设备尚未同步'}>
            {status.label}
          </span>
        </span>
        <span className="device-tree-col" title={device.last_seen_at ? formatAge(device.last_seen_at, now) : undefined}>
          {formatTime(device.last_seen_at)}
        </span>
        <div className="device-tree-actions">
          <button
            type="button"
            className="icon-button"
            title="复制设备"
            aria-label={`复制设备 ${device.name}`}
            onClick={(event) => { event.stopPropagation(); openCopy(device); }}
          >
            <Copy size={14} />
          </button>
          <ChevronRight size={15} />
        </div>
      </div>
    );
  }

  function renderGroupRow(group: Group, depth: number) {
    const isOpen = !collapsedGroups.has(group.id);
    const childGroups = groupChildren.get(group.id) ?? [];
    const childDevices = deviceByGroup.get(group.id) ?? [];
    return (
      <Fragment key={group.id}>
        <div className="device-tree-row group-row" style={{ '--depth': depth } as CSSProperties}>
          <span className="device-tree-name group">
            <button type="button" className="device-tree-toggle" onClick={() => toggleGroup(group.id)} aria-expanded={isOpen} aria-label={isOpen ? '折叠分组' : '展开分组'}>
              {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            </button>
            <Folder size={16} />
            <span className="group-title"><strong>{group.name}</strong></span>
            <span className="count">{countDevicesUnder(group.id)} 台</span>
          </span>
          <div className="device-tree-actions">
            <button type="button" className="icon-button" title="新建子分组" aria-label="新建子分组" onClick={() => void createGroup(group.id)}><Plus size={14} /></button>
            <button type="button" className="icon-button" title="重命名" aria-label="重命名" onClick={() => void renameGroup(group)}><Pencil size={14} /></button>
            <button type="button" className="icon-button danger" title="删除" aria-label="删除" onClick={() => void deleteGroup(group)}><Trash2 size={14} /></button>
          </div>
        </div>
        {isOpen && childGroups.map((child) => renderGroupRow(child, depth + 1))}
        {isOpen && childDevices.map((device) => renderDeviceRow(device, depth + 1))}
      </Fragment>
    );
  }

  function renderUngroupedRow() {
    const isOpen = !collapsedGroups.has(UNGROUPED_GROUP_KEY);
    return (
      <Fragment>
        <div className="device-tree-row group-row" style={{ '--depth': 0 } as CSSProperties}>
          <span className="device-tree-name group">
            <button
              type="button"
              className="device-tree-toggle"
              onClick={() => toggleGroup(UNGROUPED_GROUP_KEY)}
              aria-expanded={isOpen}
              aria-label={isOpen ? '折叠未分组' : '展开未分组'}
            >
              {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            </button>
            {isOpen ? <FolderOpen size={16} /> : <Folder size={16} />}
            <span className="group-title"><strong>未分组</strong></span>
            <span className="count">{ungroupedDevices.length} 台</span>
          </span>
        </div>
        {isOpen && ungroupedDevices.map((device) => renderDeviceRow(device, 1))}
      </Fragment>
    );
  }

  function renderKeys() {
    return (
      <div className="tab-section">
        <div className="section-head">
          <div>
            <h3>设备密钥</h3>
            <p>仅展示前缀；secret 不落库、不再次展示。</p>
          </div>
          <div className="section-actions">
            <button type="button" className="secondary-button small" onClick={() => void loadKeys(selected!.id)}>
              <RefreshCw size={14} />刷新
            </button>
            <button type="button" className="primary-button small" onClick={() => void issue('proof')}>
              <KeyRound size={14} />生成 proof token
            </button>
            <button type="button" className="secondary-button small" onClick={() => void issue('preprovisioned')}>
              <KeyRound size={14} />生成预置 key
            </button>
          </div>
        </div>
        <div className="rows" aria-busy={keysLoading}>
          {keysLoading && <div className="empty-state"><span className="empty-icon"><RefreshCw className="spin" size={20} /></span><strong>加载密钥…</strong></div>}
          {!keysLoading && keys.length === 0 && <Empty icon={KeyRound} title="还没有已激活的设备密钥" hint="生成 proof token 或预置 key 后设备即可注册。" />}
          {keys.map((key) => (
            <article className="row" key={key.id}>
              <span className="row-icon amber"><KeyRound size={16} /></span>
              <div className="row-main">
                <strong>{key.name}</strong>
                <span className="mono">{key.key_prefix}</span>
              </div>
              <div className="row-tags">
                {key.permissions.map((permission) => <span key={permission} className="tag">{permission}</span>)}
              </div>
              <span className={`status-pill ${statusTone(key.status)}`}>{KEY_STATUS[key.status] ?? key.status}</span>
              <button type="button" className="icon-button danger" title="吊销" onClick={() => void revoke(key.key_id)}><Trash2 size={14} /></button>
            </article>
          ))}
        </div>
      </div>
    );
  }

  function renderForwarding() {
    return (
      <div className="tab-section">
        <div className="section-head">
          <div>
            <h3>消息转发</h3>
            <p>按 source / level / regex 过滤，命中后进入 outbox。</p>
          </div>
          <div className="section-actions">
            <button type="button" className="primary-button small" onClick={() => setRuleOpen(true)}><Plus size={14} />新建规则</button>
          </div>
        </div>
        <div className="rows" aria-busy={rulesLoading}>
          {rulesLoading && <div className="empty-state"><span className="empty-icon"><RefreshCw className="spin" size={20} /></span><strong>加载规则…</strong></div>}
          {!rulesLoading && rules.length === 0 && <Empty icon={Send} title="还没有转发规则" hint="创建规则后，匹配的日志/事件会推送到指定通道。" />}
          {rules.map((rule) => (
            <article className="row" key={rule.id}>
              <span className={`row-icon ${rule.enabled ? 'violet' : 'muted'}`}><BellRing size={16} /></span>
              <div className="row-main">
                <strong>{rule.name}</strong>
                <span>{SOURCE_LABEL[rule.source] ?? rule.source} → {CHANNEL_LABEL[rule.channel] ?? rule.channel}
                  {' · '}{rule.filter.levels?.join(',') || '全部级别'}{rule.filter.message_regex ? ` · /${rule.filter.message_regex}/` : ''}</span>
              </div>
              <button type="button" className="icon-button danger" title="删除" onClick={() => void deleteRule(rule.id)}><Trash2 size={14} /></button>
            </article>
          ))}
        </div>
      </div>
    );
  }

  function renderFiles() {
    return (
      <div className="tab-section">
        <div className="section-head">
          <div>
            <h3>设备文件</h3>
            <p className="mono">{filePath.length === 0 ? '根目录' : `根目录 / ${filePath.join(' / ')}`}</p>
          </div>
          <div className="section-actions">
            {fileParent && <button type="button" className="secondary-button small" onClick={backToRoot}><FolderOpen size={14} />返回根目录</button>}
            <button type="button" className="secondary-button small" onClick={() => void createFolder()}><Plus size={14} />新建文件夹</button>
            <label className="secondary-button small">
              <Plus size={14} />上传文件
              <input type="file" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) void uploadFile(file); event.currentTarget.value = ''; }} />
            </label>
          </div>
        </div>
        <div className="rows" aria-busy={filesLoading}>
          {filesLoading && <div className="empty-state"><span className="empty-icon"><RefreshCw className="spin" size={20} /></span><strong>加载文件…</strong></div>}
          {!filesLoading && files.length === 0 && <Empty icon={Folder} title="当前目录为空" hint="上传文件或新建文件夹开始。" />}
          {files.map((entry) => (
            <article className="row" key={entry.id}>
              <span className="row-icon">{entry.kind === 'folder' ? <Folder size={16} /> : <FileText size={16} />}</span>
              <div className="row-main">
                <strong>{entry.name}</strong>
                <span>{entry.kind === 'folder' ? '文件夹' : formatBytes(entry.size_bytes)}{entry.content_type ? ` · ${entry.content_type}` : ''}</span>
              </div>
              <div className="section-actions">
                {entry.kind === 'folder' && <button type="button" className="text-button" onClick={() => openFolder(entry)}>打开</button>}
                {entry.kind === 'file' && <a className="text-button" href={`/api/devices/${encodeURIComponent(selected!.id)}/files/${encodeURIComponent(entry.id)}/content`}>下载</a>}
                <button type="button" className="icon-button danger" title="删除" onClick={() => void deleteFile(entry.id)}><Trash2 size={14} /></button>
              </div>
            </article>
          ))}
        </div>
      </div>
    );
  }

  function renderMessages() {
    return (
      <div className="tab-section">
        <div className="section-head">
          <div>
            <h3>设备消息</h3>
            <p>拉取不消费，设备 ack 后才推进；agent 在每次 sync 后拉取，并把内容写入设备日志。</p>
          </div>
          <div className="section-actions">
            <button type="button" className="secondary-button small" onClick={() => void loadMessages(selected!.id)}>
              <RefreshCw size={14} />刷新
            </button>
          </div>
        </div>
        <form className="inline-form" onSubmit={(event) => { event.preventDefault(); void createMessage(); }}>
          <input value={messageTitle} onChange={(event) => setMessageTitle(event.target.value)} placeholder="标题" aria-label="消息标题" />
          <input value={messageContent} onChange={(event) => setMessageContent(event.target.value)} placeholder="内容" aria-label="消息内容" />
          <button type="submit" className="primary-button small" disabled={!messageTitle.trim()}>下发</button>
        </form>
        <div className="rows" aria-busy={messagesLoading}>
          {!messagesLoading && messages.length === 0 && <Empty icon={MessageSquare} title="还没有设备消息" hint="填写标题与内容后下发；设备下次 sync 会拉取并确认，日志里也能看到。" />}
          {messages.map((message) => (
            <article className="row" key={message.id}>
              <span className="row-icon blue"><MessageSquare size={16} /></span>
              <div className="row-main">
                <strong>{message.title}</strong>
                <span>{formatTime(message.created_at)}</span>
              </div>
              <span className={`status-pill ${message.status === 'active' ? 'warn' : statusTone(message.status)}`}>{MSG_STATUS[message.status] ?? message.status}</span>
              {message.status === 'active' && <button type="button" className="text-button danger" onClick={() => void cancelMessage(message.id)}>取消</button>}
            </article>
          ))}
        </div>
      </div>
    );
  }

  function renderScripts() {
    return (
      <div className="tab-section">
        <div className="ide-shell embedded" aria-busy={scriptsLoading || scriptBusy}>
          <aside className="ide-tree">
            <header className="ide-tree-head">
              <div><strong>脚本</strong><span>{scripts.length} 个</span></div>
              <button type="button" className="icon-button" title="新建脚本" aria-label="新建脚本" onClick={() => setScriptModalOpen(true)}><Plus size={16} /></button>
            </header>
            <div className="ide-tree-search">
              <input value={scriptQuery} onChange={(event) => setScriptQuery(event.target.value)} placeholder="搜索脚本" aria-label="搜索脚本" />
            </div>
            <div className="ide-tree-body">
              {scriptsLoading && <p className="empty">加载脚本…</p>}
              {!scriptsLoading && filteredScripts.length === 0 && <p className="empty">{scriptQuery ? '没有匹配脚本' : '还没有脚本'}</p>}
              {filteredScripts.map((script) => (
                <button type="button" key={script.id} className={`ide-tree-row file${script.id === selectedScriptID ? ' active' : ''}`} title={script.description || script.name} onClick={() => void openScript(script)}>
                  <FileCode2 size={14} />
                  <span>{script.name}</span>
                  {script.active_version !== null && <i title={`active v${script.active_version}`} />}
                </button>
              ))}
            </div>
            <footer className="ide-tree-foot"><Code2 size={13} />仅当前设备可读</footer>
          </aside>

          <section className="ide-main">
            <header className="ide-toolbar">
              <div className="ide-file-title">
                <strong title={scriptDescription || scriptName}>{scriptName || '未选择脚本'}</strong>
                <span>{scriptLanguage}{scriptDirty ? ' · 有未保存修改' : ''}{scriptActiveVersion ? ` · active v${scriptActiveVersion}` : ''}</span>
              </div>
              <div className="ide-toolbar-actions">
                <button type="button" className="secondary-button small" disabled={!selectedScriptID || !scriptURL} onClick={() => void copyScriptURL()}><Copy size={14} />复制 URL</button>
                <button type="button" className="secondary-button small" disabled={!selectedScriptID || scriptBusy} title={scriptDirty ? '运行当前查看版本；未保存修改不会下发' : '运行当前查看版本'} onClick={() => void runScriptNow()}><Play size={14} />立即执行</button>
                <button type="button" className="secondary-button small" disabled={!selectedScriptID || scriptBusy} onClick={() => { setScriptRunsOpen(true); void refreshScriptRuns(); }}><History size={14} />执行历史</button>
                <button type="button" className="secondary-button small" disabled={!selectedScriptID || scriptBusy} onClick={() => void saveScriptVersion(false)}><Save size={14} />保存版本</button>
                <button type="button" className="primary-button small" disabled={!selectedScriptID || scriptBusy} onClick={() => void saveScriptVersion(true)}><Save size={14} />保存并激活</button>
                <Select aria-label="查看版本" className="compact-select" value={scriptViewVersion ?? ''} disabled={!selectedScriptID || scriptBusy} onChange={(event) => {
                  const script = scripts.find((item) => item.id === selectedScriptID);
                  const value = Number(event.target.value);
                  if (script && value) void openScript(script, value);
                }}>
                  {scriptViewVersion !== null && !scriptVersions.some((version) => version.version === scriptViewVersion) && <option value={scriptViewVersion}>v{scriptViewVersion}</option>}
                  {scriptVersions.map((version) => <option key={version.version} value={version.version}>v{version.version}</option>)}
                </Select>
                {scriptViewVersion !== null && scriptViewVersion !== scriptActiveVersion && <button type="button" className="text-button" disabled={scriptBusy} onClick={() => void activateScriptVersion()}>激活 v{scriptViewVersion}</button>}
                <button type="button" className="icon-button" title="脚本设置" aria-label="脚本设置" disabled={!selectedScriptID} onClick={() => { setScriptSettings({ name: scriptName, description: scriptDescription, language: scriptLanguage }); setScriptSettingsOpen(true); }}><Settings2 size={16} /></button>
                <button type="button" className="icon-button danger" title="删除脚本" aria-label="删除脚本" disabled={!selectedScriptID} onClick={() => void deleteScript()}><Trash2 size={16} /></button>
              </div>
            </header>
            <div className="ide-editor">
              {!selectedScriptID && <div className="tenant-empty"><FileCode2 size={32} /><strong>选择左侧脚本</strong><span>或点击 + 新建设备脚本。</span></div>}
              {selectedScriptID && <CodeEditor value={scriptContent} language={scriptLanguage} onChange={setScriptContent} height="100%" />}
            </div>
            <footer className="ide-status">
              <span>{scriptName || '—'}</span>
              <span>{scriptViewVersion ? `查看 v${scriptViewVersion}` : ''}{scriptActiveVersion ? ` · active v${scriptActiveVersion}` : ''}</span>
              <span>{scriptDirty ? '未保存' : '已保存'}</span>
            </footer>
          </section>
        </div>
      </div>
    );
  }

  function renderInstall() {
    return (
      <div className="tab-section">
        <div className="section-head">
          <div><h3>安装脚本</h3><p>注册前公开获取；版本保存后立即成为 active。</p></div>
          <div className="section-actions">
            <Select aria-label="选择 install 版本" className="compact-select" value={installVersion} onChange={(event) => void openInstallVersion(event.target.value)}>
              {installVersions.map((version) => <option key={version.version} value={version.version}>v{version.version}</option>)}
            </Select>
            <button type="button" className="secondary-button small" disabled={commandBusy} onClick={() => void makeCommand('proof')}><TerminalSquare size={14} />一键命令</button>
            <button type="button" className="primary-button small" disabled={assetBusy || !installScript.trim()} onClick={() => void saveInstall()}><Save size={14} />保存版本</button>
          </div>
        </div>
        <CodeEditor value={installScript} language="shell" onChange={setInstallScript} height="360px" />
        <p className="script-meta">{installVersion ? `当前版本 v${installVersion} · ${installSHA.slice(0, 16)}…` : '还没有 install 版本'} · 版本输入框可改成自定义版本号（如 1.1）。</p>
        <div className="inline-actions">
          <input value={installVersion} onChange={(event) => setInstallVersion(event.target.value)} placeholder="版本号，例如 1.0" aria-label="install 版本号" />
          <span className="card-hint">保存会用该版本号创建/覆盖 active。</span>
        </div>
      </div>
    );
  }

  function renderBoot() {
    return (
      <div className="tab-section">
        <div className="section-head">
          <div><h3>Boot 脚本</h3><p>boot_shell 启动钩子；版本保存后立即成为 active。</p></div>
          <div className="section-actions">
            <Select aria-label="选择 boot 版本" className="compact-select" value={bootVersion} onChange={(event) => void openBootVersion(event.target.value)}>
              {bootVersions.map((version) => <option key={version.version} value={version.version}>v{version.version}</option>)}
            </Select>
            <button type="button" className="primary-button small" disabled={assetBusy || !bootScript.trim()} onClick={() => void saveBoot()}><Save size={14} />保存版本</button>
          </div>
        </div>
        <CodeEditor value={bootScript} language="shell" onChange={setBootScript} height="300px" />
        <label className="stack-form"><span>settings JSON</span><textarea value={bootSettings} onChange={(event) => setBootSettings(event.target.value)} rows={4} /></label>
        <div className="inline-actions">
          <input value={bootVersion} onChange={(event) => setBootVersion(event.target.value)} placeholder="版本号，例如 1.0" aria-label="boot 版本号" />
          <span className="script-meta">{bootVersion ? `当前 v${bootVersion} · ${bootSHA.slice(0, 16)}…` : '还没有 boot 版本'}</span>
        </div>
        <div className="section-head"><div><h3>Boot 运行历史</h3><p>设备启动后上报的 boot 执行结果。</p></div></div>
        <div className="script-runs">
          {bootRuns.length === 0 && <p className="empty">还没有 boot 运行记录。</p>}
          {bootRuns.map((run) => <article className="script-run" key={run.id}>
            <span className={`status-pill ${run.status === 'ok' ? 'ok' : 'danger'}`}>{run.status}</span>
            <span>v{run.boot_version} · {formatTime(run.created_at)}</span>
            <span>{run.exit_code !== null ? `exit ${run.exit_code}` : '—'}</span>
          </article>)}
        </div>
        {bootRuns[0] && <div className="script-output"><header><span>最近输出</span><span>{bootRuns[0].error_code || bootRuns[0].status}</span></header><pre>{bootRuns[0].stdout || bootRuns[0].stderr || '暂无输出'}</pre></div>}
      </div>
    );
  }

  function renderIPHistory() {
    if (!selected) return null;
    return (
      <div className="tab-section">
        <div className="section-head">
          <div>
            <h3>IP 同步历史</h3>
            <p>设备每次调用 update_ip 都会记录；地址没有变化也会保留，便于确认真实同步节奏。</p>
          </div>
          <div className="section-actions">
            <span className="count-chip">{ipHistory.length} 条</span>
            <button type="button" className="secondary-button small" disabled={ipHistoryLoading} onClick={() => void loadIPHistory(selected.id)}>
              <RefreshCw className={ipHistoryLoading ? 'spin' : ''} size={14} />刷新
            </button>
          </div>
        </div>
        <div className="panel tenant-table-wrap ip-history-table" aria-busy={ipHistoryLoading}>
          <div className="tenant-table-head ip-history-grid">
            <span>时间</span>
            <span>地址</span>
            <span>类型</span>
            <span>来源</span>
            <span>结果</span>
          </div>
          {ipHistoryLoading && ipHistory.length === 0 && (
            <div className="panel-loader" role="status">正在加载同步历史…</div>
          )}
          {!ipHistoryLoading && ipHistory.length === 0 && (
            <div className="empty-state">
              <span className="empty-icon"><History size={20} /></span>
              <strong>还没有 IP 同步记录</strong>
              <span>设备下一次上报 IP 后，这里会显示完整历史。</span>
            </div>
          )}
          {ipHistory.map((item) => (
            <div className="tenant-table-row ip-history-grid" key={item.id}>
              <time>{formatTime(item.reported_at)}</time>
              <span className="mono">{item.ip}</span>
              <span><span className="tag">{item.type}</span></span>
              <span>{IP_SOURCE_LABEL[item.source] ?? item.source}</span>
              <span>
                <span className={`status-pill ${item.changed ? 'ok' : 'neutral'}`}>
                  {item.changed ? '已更新' : '未变化'}
                </span>
              </span>
            </div>
          ))}
          {ipHistoryNext !== null && (
            <footer className="pagination-bar">
              <span>每次加载 50 条</span>
              <button type="button" className="secondary-button small" disabled={ipHistoryLoading} onClick={() => void loadIPHistory(selected.id, ipHistoryNext)}>
                {ipHistoryLoading ? '加载中…' : '加载更早记录'}
              </button>
            </footer>
          )}
        </div>
      </div>
    );
  }

  function renderOverview() {
    if (!selected) return null;
    const groupName = selected.group_id ? (groupPathMap.get(selected.group_id) ?? '未分组') : '未分组';
    const status = deviceStatus(selected, now);
    return (
      <div className="tab-section">
        <div className="overview-hero">
          <span className="overview-icon"><Server size={22} /></span>
          <div className="overview-title">
            <div className="overview-name">
              <h2>{selected.name}</h2>
              <span className={`status-pill ${status.tone}`} title={selected.last_seen_at ? `最近同步 ${formatTime(selected.last_seen_at)}` : '设备尚未同步'}>
                {status.label}
              </span>
            </div>
            <p className="mono">{selected.id}</p>
          </div>
          <div className="overview-actions">
            <button type="button" className="primary-button small" onClick={() => void issue('proof')}><KeyRound size={14} />生成 proof token</button>
            <button type="button" className="secondary-button small" onClick={() => void issue('preprovisioned')}><KeyRound size={14} />生成预置 key</button>
            <button type="button" className="secondary-button small" onClick={() => void makeCommand('proof')}><TerminalSquare size={14} />安装命令</button>
            <button type="button" className="secondary-button small" onClick={() => openEdit(selected)}><Pencil size={14} />编辑设备</button>
            <button type="button" className="secondary-button small" onClick={() => openCopy(selected)}><Copy size={14} />复制设备</button>
          </div>
        </div>

        <dl className="meta-grid">
          <div><dt>FQDN</dt><dd>{selected.fqdn ?? '—（IP-only）'}</dd></div>
          <div><dt>Identify</dt><dd className="mono" title={selected.identify ?? '未设置'}>{selected.identify ?? '未设置'}</dd></div>
          <div><dt>网络模式</dt><dd>{selected.network_mode === 'ddns' ? 'DDNS' : '仅 IP 上报'}</dd></div>
          <div><dt>分组</dt><dd>{groupName}</dd></div>
          <div><dt>创建时间</dt><dd>{formatTime(selected.created_at)}</dd></div>
          <div><dt>最近同步</dt><dd>{selected.last_seen_at ? `${formatAge(selected.last_seen_at, now)}（${formatTime(selected.last_seen_at)}）` : '从未同步'}</dd></div>
          {selected.notes && <div className="wide"><dt>备注</dt><dd>{selected.notes}</dd></div>}
        </dl>

        <div className="card-grid">
          <section className="card">
            <header className="card-head">
              <span className="row-icon blue"><Globe size={15} /></span>
              <h3>网络</h3>
              <div className="card-head-actions">
                <button type="button" className="text-button" onClick={() => selectTab('history')}><History size={13} />同步历史</button>
                <button type="button" className="text-button" onClick={() => void loadIP(selected.id)}><RefreshCw size={13} />刷新</button>
              </div>
            </header>
            <div className="card-body">
              <div className="ip-line">
                <Wifi size={16} />
                <div>
                  <strong>{ipInfo ? `${ipInfo.ip}` : '暂无上报'}</strong>
                  <span>{ipInfo ? `${ipInfo.type} · ${ipInfo.source}` : '设备尚未上报 IP'}</span>
                </div>
              </div>
              <div className="ip-meta">
                <span>最近上报</span>
                <span className="mono">{ipInfo ? formatTime(ipInfo.reported_at) : '—'}</span>
              </div>
            </div>
          </section>

          <section className="card">
            <header className="card-head">
              <span className="row-icon violet"><Code2 size={15} /></span>
              <h3>设备模板</h3>
            </header>
            <div className="card-body">
              <p className="card-hint">应用模板会为该设备新增 install / boot 版本。</p>
              <div className="inline-actions">
                <Select aria-label="选择模板" value={templateID} onChange={(event) => setTemplateID(event.target.value)}>
                  <option value="">选择模板…</option>
                  {templates.map((template) => <option key={template.id} value={template.id}>{template.name} v{template.current_version}</option>)}
                </Select>
                <button type="button" className="secondary-button small" disabled={!templateID} onClick={() => void applyTemplate()}>应用</button>
                <button type="button" className="text-button" onClick={() => setTemplateOpen(true)}><Plus size={13} />新建模板</button>
              </div>
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <section className="device-center">
      {!selected && (
        <>
          {error && <p className="filter-error" role="alert">{error}</p>}

          <div className="panel device-tree-panel" aria-busy={loading}>
            <div className="device-tree-toolbar">
              <div className="search-box">
                <Search size={14} />
                <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索设备…" aria-label="搜索设备" />
              </div>
              <span className="count-chip">{query.trim() ? `${visibleDevices.length} / ${devices.length}` : `${devices.length} 台设备`}</span>
              <div className="device-center-actions">
                <button
                  type="button"
                  className="secondary-button small"
                  onClick={expandAllGroups}
                  disabled={loading || query.trim() !== '' || allGroupIDs.length === 0}
                  title="展开全部目录"
                >
                  <ChevronsUpDown size={14} />全部展开
                </button>
                <button
                  type="button"
                  className="secondary-button small"
                  onClick={collapseAllGroups}
                  disabled={loading || query.trim() !== '' || allGroupIDs.length === 0}
                  title="收起全部目录"
                >
                  <ChevronsDownUp size={14} />全部收起
                </button>
                <button type="button" className="secondary-button small" onClick={() => void createGroup()}>新建分组</button>
                <button type="button" className="secondary-button small" onClick={() => void loadDevices()}><RefreshCw size={14} />刷新</button>
                <button type="button" className="primary-button small" onClick={() => setCreateOpen(true)}><Plus size={15} />新建设备</button>
              </div>
            </div>
            <div className="device-tree-table">
              <div className="device-tree-head">
                <span>名称</span>
                <span>IP 地址</span>
                <span>状态</span>
                <span>最近更新</span>
                <span />
              </div>
              <div className="device-tree-body">
                {loading && <div className="empty-state"><span className="empty-icon"><RefreshCw className="spin" size={20} /></span><strong>加载中…</strong></div>}

                {!loading && query.trim() !== '' && (
                  visibleDevices.length === 0
                    ? <Empty icon={Server} title="没有匹配的设备" hint="换个关键词试试。" />
                    : visibleDevices.map((device) => renderDeviceRow(device, 0, true))
                )}

                {!loading && query.trim() === '' && (
                  <>
                    {devices.length === 0 && groups.length === 0 && (
                      <Empty icon={Server} title="还没有设备" hint="新建设备后生成 proof token 或预置 key。" />
                    )}
                    {rootGroups.map((group) => renderGroupRow(group, 0))}
                    {ungroupedDevices.length > 0 && renderUngroupedRow()}
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {selected && (
        <div className="panel device-detail-page">
          <nav className="file-breadcrumbs device-breadcrumbs" aria-label="设备路径">
            <button type="button" onClick={goBackToList}><FolderOpen size={14} />设备中心</button>
            {selectedGroupChain.map((group) => (
              <span key={group.id}><ChevronRight size={13} /><span className="crumb-text">{group.name}</span></span>
            ))}
            <span><ChevronRight size={13} /><strong>{selected.name}</strong></span>
          </nav>
          <nav className="detail-tabs" aria-label="设备详情">
            {TABS.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  type="button"
                  key={item.id}
                  className={`detail-tab${tab === item.id ? ' active' : ''}`}
                  aria-current={tab === item.id ? 'page' : undefined}
                  onClick={() => selectTab(item.id)}
                >
                  <Icon size={15} />{item.label}
                </button>
              );
            })}
          </nav>
          <div className="detail-body">
            {tab === 'overview' && renderOverview()}
            {tab === 'history' && renderIPHistory()}
            {tab === 'console' && <DeviceConsole key={selected.id} deviceId={selected.id} notify={notify} />}
            {tab === 'logs' && <LogStream key={selected.id} apiBase={`/api/devices/${encodeURIComponent(selected.id)}/logs`} notify={notify} />}
            {tab === 'identify-logs' && (
              <IdentifyLogs
                key={selected.id}
                deviceId={selected.id}
                deviceIdentify={selected.identify}
                notify={notify}
                onEditIdentify={() => openEdit(selected)}
              />
            )}
            {tab === 'install' && renderInstall()}
            {tab === 'boot' && renderBoot()}
            {tab === 'keys' && renderKeys()}
            {tab === 'forwarding' && renderForwarding()}
            {tab === 'files' && renderFiles()}
            {tab === 'messages' && renderMessages()}
            {tab === 'scripts' && renderScripts()}
          </div>
        </div>
      )}

      {createOpen && (
        <Modal title="新建设备" subtitle="FQDN 与 Identify 可选；备注仅控制面可见。" onClose={() => setCreateOpen(false)}>
          <form className="device-form" onSubmit={(event) => { event.preventDefault(); void createDevice(); }}>
            <label><span>设备名称</span><input value={createName} onChange={(event) => setCreateName(event.target.value)} placeholder="edge-01" autoFocus /></label>
            <label><span>FQDN（可选）</span><input value={createFQDN} onChange={(event) => setCreateFQDN(event.target.value)} placeholder="edge-01.example.com" /></label>
            <label>
              <span>Identify（可选）</span>
              <span className="identify-input-row">
                <input value={createIdentify} onChange={(event) => setCreateIdentify(event.target.value)} placeholder="留空自动生成 8 位" />
                <button type="button" className="secondary-button small" onClick={() => setCreateIdentify(generateIdentify())}>生成</button>
              </span>
              <small className="field-hint">低权限日志写入标识，全局唯一、不限长度；留空时由服务端生成 8 位。</small>
            </label>
            <label><span>备注（可选）</span><textarea rows={3} maxLength={4000} value={createNotes} onChange={(event) => setCreateNotes(event.target.value)} placeholder="机房位置、负责人、用途…" /></label>
            <label><span>分组（可选）</span><Select aria-label="分组" value={createGroupID} onChange={(event) => setCreateGroupID(event.target.value)}>
              <option value="">未分组</option>
              {groupOptions.map((option) => <option key={option.id} value={option.id}>{option.path}</option>)}
            </Select></label>
            <footer>
              <button type="button" className="secondary-button" onClick={() => setCreateOpen(false)}>取消</button>
              <button type="submit" className="primary-button" disabled={creating}>{creating ? '创建中…' : '创建'}</button>
            </footer>
          </form>
        </Modal>
      )}

      {editOpen && selected && (
        <Modal title="编辑设备" subtitle="名称、备注、分组与 FQDN 都可随时修改；改 FQDN 会同步更新解析记录。" onClose={() => setEditOpen(false)}>
          <form className="device-form" onSubmit={(event) => { event.preventDefault(); void saveDevice(); }}>
            <label><span>设备名称</span><input value={editName} maxLength={80} onChange={(event) => setEditName(event.target.value)} autoFocus required /></label>
            <label><span>FQDN</span><input value={editFQDN} onChange={(event) => setEditFQDN(event.target.value)} placeholder="留空即改为仅 IP 上报" /></label>
            <label>
              <span>Identify</span>
              <span className="identify-input-row">
                <input value={editIdentify} onChange={(event) => setEditIdentify(event.target.value)} placeholder="留空表示停用 Identify 写入" />
                <button type="button" className="secondary-button small" onClick={() => setEditIdentify(generateIdentify())}>生成</button>
              </span>
              <small className="field-hint">清空会停止通过该 Identify 写入新日志，已有日志仍保留。</small>
            </label>
            <label><span>备注</span><textarea rows={4} maxLength={4000} value={editNotes} onChange={(event) => setEditNotes(event.target.value)} placeholder="机房位置、负责人、用途…" /></label>
            <label><span>分组</span><Select aria-label="分组" value={editGroupID} onChange={(event) => setEditGroupID(event.target.value)}>
              <option value="">未分组</option>
              {groupOptions.map((option) => <option key={option.id} value={option.id}>{option.path}</option>)}
            </Select></label>
            {editFQDN.trim() && editFQDN.trim() !== (selected.fqdn ?? '') && <p className="card-hint">保存后 A/AAAA 解析记录会改到新域名，旧域名不再指向该设备。</p>}
            {!editFQDN.trim() && selected.fqdn && <p className="filter-error" role="alert">清空 FQDN 会删除解析记录，并改为仅 IP 上报。</p>}
            <footer>
              <button type="button" className="secondary-button" onClick={() => setEditOpen(false)}>取消</button>
              <button type="submit" className="primary-button" disabled={savingEdit || !editName.trim()}>{savingEdit ? '保存中…' : '保存'}</button>
            </footer>
          </form>
        </Modal>
      )}

      {copySource && (
        <Modal title="复制设备" subtitle="新设备继承配置与脚本，但不继承密钥、消息、日志、运行历史和文件；Identify 会重新生成。" onClose={() => setCopySource(null)}>
          <form className="device-form" onSubmit={(event) => { event.preventDefault(); void copyDevice(); }}>
            <label><span>原设备</span><input value={copySource.name} disabled /></label>
            <label><span>新设备名称</span><input value={copyName} maxLength={80} onChange={(event) => setCopyName(event.target.value)} autoFocus required /></label>
            <label><span>FQDN（可选）</span><input value={copyFQDN} onChange={(event) => setCopyFQDN(event.target.value)} placeholder="留空则复制为 IP-only" /></label>
            <label><span>分组</span><Select aria-label="分组" value={copyGroupID} onChange={(event) => setCopyGroupID(event.target.value)}>
              <option value="">未分组</option>
              {groupOptions.map((option) => <option key={option.id} value={option.id}>{option.path}</option>)}
            </Select></label>
            <p className="card-hint">继承：备注、模板绑定、设备开关、install/boot 版本、设备脚本及版本、转发规则；分组默认沿用原设备，可在这里改选或设为未分组。FQDN 全局唯一，留空即不分配域名；设备密钥不会继承，注册后需重新签发。</p>
            {copyName.trim() === copySource.name && <p className="filter-error" role="alert">新设备名称不能与原设备相同。</p>}
            <footer>
              <button type="button" className="secondary-button" onClick={() => setCopySource(null)}>取消</button>
              <button type="submit" className="primary-button" disabled={copying || !copyName.trim() || copyName.trim() === copySource.name}>{copying ? '复制中…' : '复制'}</button>
            </footer>
          </form>
        </Modal>
      )}

      {ruleOpen && (
        <Modal title="新建转发规则" subtitle="命中规则的日志/事件会写入 outbox。" onClose={() => setRuleOpen(false)}>
          <form className="device-form" onSubmit={(event) => { event.preventDefault(); void createRule(); }}>
            <label><span>名称</span><input value={ruleForm.name} onChange={(event) => setRuleForm({ ...ruleForm, name: event.target.value })} placeholder="errors" /></label>
            <label><span>来源</span><Select aria-label="来源" value={ruleForm.source} onChange={(event) => setRuleForm({ ...ruleForm, source: event.target.value })}>
              <option value="log">日志</option><option value="ip.reported">IP 上报</option><option value="boot.failed">Boot 失败</option><option value="boot.applied">Boot 成功</option><option value="device.online">上线</option><option value="device.offline">离线</option>
            </Select></label>
            <label><span>通道</span><Select aria-label="通道" value={ruleForm.channel} onChange={(event) => setRuleForm({ ...ruleForm, channel: event.target.value })}>
              <option value="telegram">Telegram</option><option value="resend">Resend</option>
            </Select></label>
            <label><span>级别（逗号分隔，留空为全部）</span><input value={ruleForm.levels} onChange={(event) => setRuleForm({ ...ruleForm, levels: event.target.value })} placeholder="error,warn" /></label>
            <label><span>消息正则（可选）</span><input value={ruleForm.regex} onChange={(event) => setRuleForm({ ...ruleForm, regex: event.target.value })} placeholder="ddns|boot" /></label>
            <footer>
              <button type="button" className="secondary-button" onClick={() => setRuleOpen(false)}>取消</button>
              <button type="submit" className="primary-button">创建</button>
            </footer>
          </form>
        </Modal>
      )}

      {templateOpen && (
        <Modal title="新建设备模板" subtitle="模板只保存快照；应用时显式复制到设备。" onClose={() => setTemplateOpen(false)}>
          <form className="device-form" onSubmit={(event) => { event.preventDefault(); void createTemplate(); }}>
            <label><span>模板名称</span><input value={templateForm.name} onChange={(event) => setTemplateForm({ ...templateForm, name: event.target.value })} placeholder="edge-default" /></label>
            <label><span>描述</span><input value={templateForm.description} onChange={(event) => setTemplateForm({ ...templateForm, description: event.target.value })} /></label>
            <label><span>install 脚本</span><textarea rows={4} value={templateForm.install} onChange={(event) => setTemplateForm({ ...templateForm, install: event.target.value })} placeholder="#!/bin/sh" /></label>
            <label><span>boot 脚本</span><textarea rows={4} value={templateForm.boot} onChange={(event) => setTemplateForm({ ...templateForm, boot: event.target.value })} placeholder="#!/bin/sh" /></label>
            <footer>
              <button type="button" className="secondary-button" onClick={() => setTemplateOpen(false)}>取消</button>
              <button type="submit" className="primary-button">创建</button>
            </footer>
          </form>
        </Modal>
      )}

      {scriptModalOpen && (
        <Modal title="新建设备脚本" subtitle="创建后生成 v1；后续保存会生成新版本。" onClose={() => setScriptModalOpen(false)}>
          <form className="device-form" onSubmit={(event) => { event.preventDefault(); void createScript(); }}>
            <label><span>名称</span><input autoFocus value={newScript.name} onChange={(event) => setNewScript({ ...newScript, name: event.target.value })} placeholder="maintenance" /></label>
            <label><span>描述</span><input value={newScript.description} onChange={(event) => setNewScript({ ...newScript, description: event.target.value })} /></label>
            <label><span>语言</span><Select aria-label="语言" value={newScript.language} onChange={(event) => setNewScript({ ...newScript, language: event.target.value })}>
              <option value="shell">Shell</option><option value="python">Python</option><option value="javascript">JavaScript</option>
            </Select></label>
            <CodeEditor value={newScript.content} language={newScript.language} onChange={(value) => setNewScript({ ...newScript, content: value })} height="220px" />
            <footer>
              <button type="button" className="secondary-button" onClick={() => setScriptModalOpen(false)}>取消</button>
              <button type="submit" className="primary-button" disabled={scriptBusy || !newScript.name.trim() || !newScript.content.trim()}>创建</button>
            </footer>
          </form>
        </Modal>
      )}

      {scriptSettingsOpen && selectedScript && (
        <Modal title="脚本设置" subtitle={scriptName} onClose={() => setScriptSettingsOpen(false)}>
          <form className="device-form" onSubmit={(event) => { event.preventDefault(); void updateScriptSettings(); }}>
            <label><span>名称</span><input value={scriptSettings.name} onChange={(event) => setScriptSettings({ ...scriptSettings, name: event.target.value })} /></label>
            <label><span>描述</span><input value={scriptSettings.description} onChange={(event) => setScriptSettings({ ...scriptSettings, description: event.target.value })} /></label>
            <label><span>语言</span><Select aria-label="语言" value={scriptSettings.language} onChange={(event) => setScriptSettings({ ...scriptSettings, language: event.target.value })}>
              <option value="shell">Shell</option><option value="python">Python</option><option value="javascript">JavaScript</option>
            </Select></label>
            <div className="ide-version-list">
              <strong>版本</strong>
              {scriptVersions.map((version) => <button type="button" key={version.version} className={`ide-version-row${version.version === scriptActiveVersion ? ' active' : ''}`} onClick={() => void openScript(selectedScript, version.version)}>
                <span>v{version.version}</span><code>{version.sha256.slice(0, 12)}…</code><span>{new Date(version.created_at).toLocaleString('zh-CN', { hour12: false })}</span>
              </button>)}
            </div>
            <footer>
              <button type="button" className="secondary-button" onClick={() => setScriptSettingsOpen(false)}>取消</button>
              <button type="submit" className="primary-button" disabled={scriptBusy || !scriptSettings.name.trim()}>保存设置</button>
            </footer>
          </form>
        </Modal>
      )}

      {scriptRunsOpen && (
        <Modal title="执行历史" subtitle={`${scriptName || '当前脚本'} · 命令下发后由设备拉取，ack 后回写结果。`} size="wide-modal" onClose={() => setScriptRunsOpen(false)}>
          <div className="script-history">
            <div className="script-history-bar">
              <span>{scriptRuns.length} 条记录</span>
              <button type="button" className="secondary-button small" disabled={scriptRunsLoading} onClick={() => void refreshScriptRuns()}><RefreshCw className={scriptRunsLoading ? 'spin' : ''} size={14} />刷新</button>
            </div>
            <div className="script-runs" aria-busy={scriptRunsLoading}>
              {!scriptRunsLoading && scriptRuns.length === 0 && <p className="empty">还没有执行记录。</p>}
              {scriptRuns.map((run) => <button type="button" key={run.id} className={`script-run${selectedRun?.id === run.id ? ' active' : ''}`} onClick={() => setSelectedRun(run)}>
                <span className={`status-pill ${runTone(run.status)}`}>{RUN_STATUS[run.status] ?? run.status}</span>
                <span>v{run.version} · {formatTime(run.created_at)}</span>
                <span>{run.exit_code !== null ? `exit ${run.exit_code}` : '—'}</span>
              </button>)}
            </div>
            {selectedRun && <div className="script-output">
              <header><span>输出 · v{selectedRun.version}</span><span>{selectedRun.error_code || RUN_STATUS[selectedRun.status] || selectedRun.status}</span></header>
              <pre>{selectedRun.output || (selectedRun.status === 'queued' ? '等待设备拉取…' : '暂无输出')}</pre>
            </div>}
          </div>
        </Modal>
      )}

      {secret && (
        <Modal title={secret.title} subtitle="关闭后无法再次查看，请立即保存。" onClose={() => setSecret(null)}>
          <div className="secret-panel">
            <pre>{secret.value}</pre>
            <button type="button" className="primary-button small" onClick={() => void copy(secret.value)}><Copy size={14} />复制</button>
          </div>
        </Modal>
      )}

      {command && (
        <Modal
          title="一键安装命令"
          subtitle={command.mode === 'preprovisioned'
            ? '命令含长期设备 key，勿分享、勿写入公开日志。'
            : '命令包含一次性凭据；不要写入公开日志。'}
          onClose={() => setCommand(null)}
        >
          <div className="inline-actions">
            <span className="card-hint">下载器</span>
            <Select
              aria-label="下载器"
              className="compact-select"
              value={commandFetch}
              disabled={commandBusy}
              onChange={(event) => setCommandFetch(event.target.value as 'curl' | 'wget')}
            >
              <option value="curl">curl</option>
              <option value="wget" disabled={!command.commands?.wget}>wget</option>
            </Select>
            <span className="card-hint">凭据</span>
            <Select
              aria-label="凭据方式"
              className="compact-select"
              value={command.mode}
              disabled={commandBusy}
              onChange={(event) => void makeCommand(event.target.value as 'proof' | 'preprovisioned')}
            >
              <option value="proof">enroll-token</option>
              <option value="preprovisioned">预置 key</option>
            </Select>
          </div>
          <div className="secret-panel">
            <pre>{command.commands?.[commandFetch] ?? command.command}</pre>
            <button type="button" className="primary-button small" onClick={() => void copy(command.commands?.[commandFetch] ?? command.command)}><Copy size={14} />复制</button>
          </div>
          <p className="card-hint">
            切换下载器不会重新签发凭据；切换凭据方式会重新签发一次。
            {commandFetch === 'wget' ? ' BusyBox wget 需带 TLS 支持（libustream/uclient-fetch）。' : ''}
          </p>
          {command.expires_at && <p className="card-hint">凭据有效至 {formatTime(command.expires_at)}。</p>}
        </Modal>
      )}

      {dialog && dialog.kind === 'confirm' && (
        <ConfirmDialog
          title={dialog.title}
          message={dialog.message}
          confirmLabel={dialog.confirmLabel}
          danger={dialog.danger}
          busy={dialogBusy}
          onConfirm={() => void submitDialog()}
          onCancel={() => setDialog(null)}
        />
      )}

      {dialog && dialog.kind === 'prompt' && (
        <PromptDialog
          title={dialog.title}
          label={dialog.label}
          initial={dialog.initial}
          placeholder={dialog.placeholder}
          busy={dialogBusy}
          onSubmit={(value) => void submitDialog(value)}
          onCancel={() => setDialog(null)}
        />
      )}
    </section>
  );
}
